import { createInstance, getInstance } from '@module-federation/runtime';

/**
 * The complete Vue namespace, resolved through a dynamic import and memoized.
 *
 * Deliberately NOT a static `import * as Vue from 'vue'`: this package ships as
 * plain ES modules with `vue` external, and a static namespace import of a
 * module whose exports a consumer's bundler cannot enumerate (vue is typically a
 * runtime-shared module there) forces that bundler to RECONSTRUCT the namespace
 * object when it scope-hoists — a reconstruction that can only cover the export
 * names it saw statically, silently dropping the rest for every module wired
 * through it. A dynamic import always yields the real, complete runtime
 * namespace, which is exactly what the federation share contract below hands out.
 */
let vueNamespacePromise: Promise<typeof import('vue')> | undefined;

function loadVueNamespace(): Promise<typeof import('vue')> {
  vueNamespacePromise ??= import('vue');
  return vueNamespacePromise;
}

/**
 * Name the shell host gives its Module Federation 2 runtime instance
 * (`createInstance({ name: 'shell', … })` in the shell bootstrap). Reusing this
 * instance lets an MFE inherit the host's already-verified remote manifests and
 * its Vue share scope instead of creating a second instance that would re-fetch,
 * re-verify, and resolve a duplicate Vue.
 */
export const HOST_FEDERATION_INSTANCE_NAME = 'shell';

/**
 * The subset of the MF2 runtime host instance this package consumes. Declared
 * structurally rather than against the runtime's `ModuleFederation` class so the
 * consumer surface stays decoupled from MF2 internal type paths — the same
 * boundary discipline the shell applies to its security plugin.
 */
export interface FederationHostInstance {
  loadRemote<T = unknown>(id: string): Promise<T | null>;
  registerRemotes(
    remotes: Array<{ name: string; entry: string }>,
    options?: { force?: boolean },
  ): void;
  /** The runtime's resolved options — read-only, used to test remote registration. */
  options?: { remotes?: Array<{ name?: string; entry?: string }> };
}

/**
 * Whether `instance` already has a remote registered under exactly `name`.
 *
 * The runtime can only serve remotes it knows: `loadRemote` on an unregistered
 * name throws (RUNTIME-004) instead of falling back. Callers that cannot
 * register the remote themselves (no entry URL) test this before choosing the
 * runtime path over the classic `window[scope]` container protocol.
 *
 * Deliberately name-only and exact: alias- or prefix-registered remotes are not
 * matched, because the loader's own force re-registration evicts by exact name —
 * blessing looser matches would let a stale alias entry shadow every later
 * re-registration. A conservative miss just means the classic fallback.
 */
export function instanceHasRemote(instance: FederationHostInstance, name: string): boolean {
  if (!name) return false;
  return !!instance.options?.remotes?.some((remote) => remote.name === name);
}

/**
 * Return the host's Module Federation 2 runtime instance from the global
 * federation registry, or `undefined` when no such instance exists — a
 * standalone dev-shell serve, a legacy classic-container host, or an SSR context
 * where the runtime never bootstrapped.
 *
 * The registry lives on `globalThis.__FEDERATION__`, a singleton shared across
 * every bundled copy of `@module-federation/runtime`, so this resolves the
 * shell's instance even though a consuming MFE imports its own copy (the runtime
 * version is pinned in lockstep across the fleet). Matching is by `options.name`,
 * the same key the runtime itself uses to dedupe instances.
 *
 * Never throws: `getInstance` reads `globalThis.__FEDERATION__`, which can be
 * absent before the runtime loads, so the access is guarded. Callers treat
 * `undefined` as "no host instance — fall back to the classic window[scope] path".
 */
export function getHostFederationInstance(
  name: string = HOST_FEDERATION_INSTANCE_NAME,
): FederationHostInstance | undefined {
  try {
    const instance = getInstance((candidate) => candidate.options.name === name);
    return (instance as FederationHostInstance | null) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Build a Module Federation 2 runtime host instance named
 * {@link HOST_FEDERATION_INSTANCE_NAME}, declaring `vue` as an **eager singleton**
 * pointing at the consumer's own loaded Vue. The eager-singleton Vue is what keeps
 * every remote loaded through this instance resolving the ONE Vue already in the
 * graph instead of a second copy — a duplicate Vue breaks reactivity and
 * provide/inject across every component the remote renders.
 *
 * Shared by {@link getStandaloneFederationInstance} (no remotes, no plugins) and
 * `createAbaxUiHost` (the abax-ui remote + injected security plugin) so the
 * instance shape — above all the Vue share config — is defined exactly once.
 *
 * Internal to this package; not part of the public API. Async because the Vue
 * namespace for the share config is resolved through {@link loadVueNamespace}.
 */
export async function buildFederationHostInstance(
  remotes: Array<{ name: string; entry: string }> = [],
  plugins: unknown[] = [],
): Promise<FederationHostInstance> {
  const Vue = await loadVueNamespace();
  const options = {
    name: HOST_FEDERATION_INSTANCE_NAME,
    remotes,
    shared: {
      vue: {
        version: Vue.version,
        lib: () => Vue,
        shareConfig: { singleton: true, requiredVersion: '^3.5.0', eager: true },
      },
    },
    plugins,
  };
  // Cast the options only: `plugins` is intentionally decoupled from MF2's internal
  // `ModuleFederationRuntimePlugin` type (the caller owns the plugin contract). The
  // result is assigned, not cast, so a runtime upgrade that changes a method this
  // package calls fails type-check instead of at runtime.
  const instance: FederationHostInstance = createInstance(
    options as unknown as Parameters<typeof createInstance>[0],
  );
  return instance;
}

/**
 * A single standalone instance per bundle. Registered under the host name so a
 * sibling consumer that later calls {@link getHostFederationInstance} discovers
 * and reuses it through the shared `__FEDERATION__.__INSTANCES__` registry
 * rather than minting a second one.
 */
let standaloneInstancePromise: Promise<FederationHostInstance> | undefined;

/**
 * The runtime instance to load a remote through when no host instance exists —
 * a standalone dev-shell serve, a legacy classic-container host that never
 * bootstrapped the runtime, or a parcel whose entry is a `.json` manifest the
 * classic `window[scope]` script path cannot serve.
 *
 * Memoized module-wide: a bundle mounts many parcels but must own exactly one
 * standalone instance (and thus one Vue share scope). The in-flight PROMISE is
 * memoized so concurrent callers observe the same instance; a rejected bring-up
 * clears the memo so a later call can retry instead of keeping a poisoned result.
 */
export function getStandaloneFederationInstance(): Promise<FederationHostInstance> {
  if (!standaloneInstancePromise) {
    standaloneInstancePromise = buildFederationHostInstance().catch((error) => {
      standaloneInstancePromise = undefined;
      throw error;
    });
  }
  return standaloneInstancePromise;
}
